/**
 * ReceivablesPanel.jsx - Finance Invoice > Receivables & aging: what each
 * customer still owes, by how late it is (current, 1-30, 31-60, 61-90, 90+
 * days past due), on a chosen day. The server works it out
 * (GET /api/finance-invoices/receivables); invoices waiting for review are
 * counted apart, never aged. A customer's row opens the invoice list on them.
 */
import { useCallback, useEffect, useState } from 'react';
import { latestReads, useLiveRefresh } from '../../live/useLive';
import { exportCsv } from '../../utils/exportCsv';
import { Msg, num, td, th } from '../spareParts/spUi';
import { Btn, LINE, MUTED, TextInput, fmtMoney } from '../quotes/quoteUi';
import { errorOf, getReceivables } from './financeInvoicesApi';
import { BUCKETS } from './invoiceRules';

const today = () => new Date().toLocaleDateString('en-CA');

export default function ReceivablesPanel({ tick = 0, onCustomer }) {
  const [asOf, setAsOf] = useState(today);
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState(null);
  const [reads] = useState(latestReads);
  const load = useCallback(async () => {
    const t = reads.start();
    const res = await getReceivables(asOf);
    if (!reads.show(t)) return;
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'Could not load the receivables.') });
      return;
    }
    setMsg(null);
    setData(res.body);
  }, [asOf, reads]);
  useEffect(() => {
    load();
  }, [load, tick]);
  useLiveRefresh('finance-invoices', load);

  const csv = () =>
    exportCsv(
      `receivables-${asOf}.csv`,
      (data.customers || []).map((c) => ({
        Customer: c.customer,
        Invoices: c.count,
        ...Object.fromEntries(BUCKETS.map(([k, l]) => [l, c[k].toFixed(2)])),
        Total: c.total.toFixed(2),
      }))
    );
  const t = data && data.totals;
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <span style={{ fontSize: 11.5, fontWeight: 600 }}>As of</span>
        <TextInput aria-label="As of" type="date" value={asOf} onChange={(v) => setAsOf(v || today())} style={{ width: 150 }} />
        <span style={{ fontSize: 11, color: MUTED }}>Days past the due date (an invoice without one is due on its invoice date).</span>
        {data && (
          <Btn small onClick={csv} disabled={!data.customers.length} style={{ marginLeft: 'auto' }}>
            Export CSV
          </Btn>
        )}
      </div>
      <Msg msg={msg} />
      {!data && !msg && <div style={{ padding: 30, color: MUTED, fontSize: 12 }}>Loading the receivables...</div>}
      {data && (
        <>
          <div style={{ border: `1px solid ${LINE}`, borderRadius: 10, overflow: 'auto', background: '#fff' }}>
            <table aria-label="Receivables by customer" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Customer</th>
                  <th style={{ ...th, textAlign: 'right' }}>Invoices</th>
                  {BUCKETS.map(([k, l]) => (
                    <th key={k} style={{ ...th, textAlign: 'right' }}>
                      {l}
                    </th>
                  ))}
                  <th style={{ ...th, textAlign: 'right' }}>Open balance</th>
                </tr>
              </thead>
              <tbody>
                {data.customers.map((c) => (
                  <tr
                    key={c.customer}
                    tabIndex={0}
                    className="go-focus"
                    style={{ cursor: 'pointer' }}
                    title={`Open ${c.customer}'s invoices`}
                    onClick={() => onCustomer(c.customer)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onCustomer(c.customer);
                    }}
                  >
                    <td style={{ ...td, fontWeight: 600 }}>{c.customer}</td>
                    <td style={num}>{c.count}</td>
                    {BUCKETS.map(([k]) => (
                      <td
                        key={k}
                        style={{
                          ...num,
                          color: c[k] && k !== 'current' ? (k === 'd90_plus' || k === 'd61_90' ? '#B71C1C' : '#92400E') : undefined,
                        }}
                      >
                        {c[k] ? fmtMoney(c[k]) : '-'}
                      </td>
                    ))}
                    <td style={{ ...num, fontWeight: 800 }}>{fmtMoney(c.total)}</td>
                  </tr>
                ))}
                {!data.customers.length && (
                  <tr>
                    <td colSpan={BUCKETS.length + 3} style={{ ...td, textAlign: 'center', color: MUTED, padding: 24 }}>
                      Nothing is owed on {asOf}.
                    </td>
                  </tr>
                )}
              </tbody>
              {data.customers.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#F6F9F3', fontWeight: 800 }}>
                    <td style={td}>Total</td>
                    <td style={num}>{t.count}</td>
                    {BUCKETS.map(([k]) => (
                      <td key={k} style={num}>
                        {fmtMoney(t[k])}
                      </td>
                    ))}
                    <td style={num}>{fmtMoney(t.total)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {data.needsReview.count > 0 && (
            <div style={{ fontSize: 11.5, color: '#7A3E00', marginTop: 10 }}>
              Not aged: {data.needsReview.count} {data.needsReview.count === 1 ? 'invoice needs' : 'invoices need'} review (
              {fmtMoney(data.needsReview.amount)} not yet confirmed) - mostly the totals carried over from before the ledger.
            </div>
          )}
        </>
      )}
    </div>
  );
}
